import React, { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  readFontScale,
  applyFontScale,
  writeFontScale,
  clampScale,
  MIN_SCALE,
  MAX_SCALE,
  STEP,
  DEFAULT_SCALE,
} from '../utils/fontScale';

/**
 * "A− 100% A+" UI text-size control (handoffs/01-design-pass.md §4).
 * Steps the document root font-size; the % label resets to 100%. Persisted
 * across reloads via utils/fontScale. Styled for the deep-green sidebar.
 */
export default function FontSizeAdjuster() {
  const [scale, setScale] = useState(() => readFontScale());

  useEffect(() => {
    applyFontScale(scale);
  }, [scale]);

  const commit = (next) => {
    const s = clampScale(next);
    applyFontScale(s);
    writeFontScale(s);
    setScale(s);
  };

  const atMin = scale <= MIN_SCALE;
  const atMax = scale >= MAX_SCALE;
  const btn =
    'h-7 w-7 flex items-center justify-center rounded-md text-eddp-cream/80 ' +
    'hover:bg-white/10 hover:text-white transition-colors disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div
      className="flex items-center justify-center gap-1 mb-2 rounded-lg bg-white/5 border border-white/10 py-1"
      role="group"
      aria-label="Text size"
    >
      <button
        type="button"
        onClick={() => commit(scale - STEP)}
        disabled={atMin}
        className={btn}
        title="Smaller text"
        aria-label="Decrease text size"
      >
        <span className="sr-only">Decrease text size</span>
        <span className="flex items-center text-xs font-bold leading-none">
          A<Minus size={9} className="ml-px" />
        </span>
      </button>
      <button
        type="button"
        onClick={() => commit(DEFAULT_SCALE)}
        className="font-mono text-[11px] text-eddp-cream/90 tabular-nums w-12 text-center hover:text-white transition-colors"
        title="Reset text size to 100%"
        aria-label={`Reset text size (currently ${scale}%)`}
      >
        {scale}%
      </button>
      <button
        type="button"
        onClick={() => commit(scale + STEP)}
        disabled={atMax}
        className={btn}
        title="Larger text"
        aria-label="Increase text size"
      >
        <span className="flex items-center text-sm font-bold leading-none">
          A<Plus size={10} className="ml-px" />
        </span>
      </button>
    </div>
  );
}
